<template>
  <div class="font-extralight flex flex-col justify-center">
    <p class="uppercase text-4xl font-bold">Challenge exceptionnel : chaque vente compte !</p>
    <p class="text-lg my-2 text-gray-600">
      Le challenge s'étend du {{ challenge.startDate }} au {{ challenge.endDate }} - Relevez le défi ! 🎯
    </p>
    <div v-if="hasAccessToAdminDashboard || isResponsible" class="p-6 bg-yellow-50 rounded-sm shadow-md my-2">
      <p class="text-lg font-semibold text-gray-800">Explication du système de récompense 💡</p>
      <p class="text-sm text-gray-600 mt-2">
        Chaque vente réalisée par les vendeurs rapporte des
        <span class="font-black text-black">points.</span>
        Une fois convertis,
        <span class="font-black text-black">{{ challenge.pointsPerEuro }} points</span>
        correspondent à
        <span class="font-black text-black">1 €.</span>
      </p>
      <p class="text-sm text-gray-600 mt-2">
        La récompense totale est ensuite répartie comme suit :
        <span class="font-black text-black">70 %</span>
        pour les vendeurs et
        <span class="font-black text-black">30 %</span>
        pour la direction et le management.
      </p>
    </div>

    <div
      v-if="(hasAccessToAdminDashboard || isResponsible) && isPresent(storePerformance)"
      class="shadow-lg bg-gray-50 p-6 my-8"
    >
      <div class="flex justify-between">
        <div class="flex space-x-4">
          <div>
            <p class="text-2xl font-bold">{{ storePerformance.salesChannel }}</p>
          </div>
        </div>

        <div class="flex space-x-6">
          <div class="text-center">
            <p class="text-4xl text-gray-400">{{ storePerformance.storeScore }}</p>
            <p class="text-sm text-gray-500">Points</p>
          </div>

          <div class="text-center">
            <p class="text-4xl font-bold">
              {{ formatPrice(storePerformance.storeRewardAmount) }}
            </p>
            <p class="text-sm text-gray-500">Récompense totale</p>
          </div>
        </div>
      </div>

      <div class="mt-6 grid grid-cols-2 gap-4">
        <div class="p-4 bg-white rounded-sm shadow-md text-center">
          <p class="text-2xl font-semibold">
            {{ formatPrice(storePerformance.sellerRewardAmount) }}
          </p>
          <p class="text-sm text-gray-500">Pour les vendeurs</p>
        </div>
        <div class="p-4 bg-white rounded-sm shadow-md text-center">
          <p class="text-2xl font-semibold">
            {{ formatPrice(storePerformance.managersRewardAmount) }}
          </p>
          <p class="text-sm text-gray-500">Pour la direction et le management</p>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 space-x-0 space-y-6 lg:mt-0 lg:grid-cols-2 lg:space-x-4 lg:space-y-0">
      <div>
        <div class="mb-4 text-xl font-bold">🏠 Classement magasin</div>
        <div
          v-if="isPresent(sellers) && sellers[0].score > 0"
          class="shadow-lg bg-gray-50 relative flex items-end justify-center p-6 space-x-6"
        >
          <div v-if="thirdStoreSeller" class="flex flex-col items-center">
            <div class="text-2xl">
              <p class="text-base mb-6">{{ thirdStoreSeller.name }} 🥉</p>
            </div>
            <div
              class="bg-purple-300 text-white rounded-lg shadow-lg flex flex-col items-center justify-center p-4 h-16 w-32"
            >
              <p class="text-md text-center mb-2">{{ thirdStoreSeller.score }} points</p>
              <p class="text-md text-center">{{ formatPrice(thirdStoreSeller.rewardAmount) }}</p>
            </div>
          </div>

          <div v-if="firstStoreSeller" class="flex flex-col items-center">
            <div class="text-2xl">
              <p class="text-base mb-6">{{ firstStoreSeller.name }} 🥇</p>
            </div>
            <div
              class="bg-purple-900 text-white rounded-lg shadow-lg flex flex-col items-center justify-center p-4 h-32 w-32"
            >
              <p class="text-md text-center mb-2">{{ firstStoreSeller.score }} points</p>
              <p class="text-md text-center">{{ formatPrice(firstStoreSeller.rewardAmount) }}</p>
            </div>
          </div>

          <div v-if="secondStoreSeller" class="flex flex-col items-center">
            <div class="text-2xl">
              <p class="text-base mb-6">{{ secondStoreSeller.name }} 🥈</p>
            </div>
            <div
              class="bg-purple-600 text-white rounded-lg shadow-lg flex flex-col items-center justify-center p-4 h-24 w-32"
            >
              <p class="text-md text-center mb-2">{{ secondStoreSeller.score }} points</p>
              <p class="text-md text-center">{{ formatPrice(secondStoreSeller.rewardAmount) }}</p>
            </div>
          </div>
        </div>
        <div v-else class="relative flex items-end justify-center bg-purple-50 p-6 rounded-lg space-x-4">
          Pas encore de ventes
        </div>
      </div>
      <div>
        <div class="mb-4 text-xl font-bold">🌍 Classement réseau</div>
        <div
          v-if="isPresent(organizationSellers) && organizationSellers[0].score > 0"
          class="shadow-lg bg-gray-50 relative flex items-end justify-center p-6 space-x-6"
        >
          <div v-if="thirdOrganizationSeller" class="flex flex-col items-center">
            <div class="text-2xl">
              <p class="text-base">{{ thirdOrganizationSeller.name }} 🥉</p>
              <p class="text-xs mb-2">{{ thirdOrganizationSeller.storeSalesChannel }}</p>
            </div>
            <div
              class="bg-purple-300 text-white rounded-lg shadow-lg flex flex-col items-center justify-center p-4 h-16 w-32"
            >
              <p class="text-md text-center mb-2">{{ thirdOrganizationSeller.score }} points</p>
              <p class="text-md text-center">{{ formatPrice(thirdOrganizationSeller.rewardAmount) }}</p>
            </div>
          </div>

          <div v-if="firstOrganizationSeller" class="flex flex-col items-center">
            <div class="text-2xl">
              <p class="text-base">{{ firstOrganizationSeller.name }} 🥇</p>
              <p class="text-xs mb-2">{{ firstOrganizationSeller.storeSalesChannel }}</p>
            </div>
            <div
              class="bg-purple-900 text-white rounded-lg shadow-lg flex flex-col items-center justify-center p-4 h-32 w-32"
            >
              <p class="text-md text-center mb-2">{{ firstOrganizationSeller.score }} points</p>
              <p class="text-md text-center">{{ formatPrice(firstOrganizationSeller.rewardAmount) }}</p>
            </div>
          </div>

          <div v-if="secondOrganizationSeller" class="flex flex-col items-center">
            <div class="text-2xl">
              <p class="text-base">{{ secondOrganizationSeller.name }} 🥈</p>
              <p class="text-xs mb-2">{{ secondOrganizationSeller.storeSalesChannel }}</p>
            </div>
            <div
              class="bg-purple-600 text-white rounded-lg shadow-lg flex flex-col items-center justify-center p-4 h-24 w-32"
            >
              <p class="text-md text-center mb-2">{{ secondOrganizationSeller.score }} points</p>
              <p class="text-md text-center">{{ formatPrice(secondOrganizationSeller.rewardAmount) }}</p>
            </div>
          </div>
        </div>

        <div v-else class="relative flex items-end justify-center bg-purple-50 p-6 rounded-lg space-x-4">
          Pas encore de ventes
        </div>
      </div>
    </div>

    <div class="mt-8 grid-cols-1 space-x-0 space-y-6 lg:grid grid-cols-2 lg:space-x-4 lg:space-y-0">
      <div v-if="isPresent(sellers)">
        <div class="mb-4 text-xl font-bold">📊 Détail du classement magasin</div>
        <div class="overflow-x-auto max-h-80 overflow-y-auto shadow-lg bg-gray-50 relative">
          <table class="table-auto w-full text-left border-collapse border text-sm">
            <tbody>
              <tr v-for="(seller, index) in sellers" :key="seller.id" :class="index % 2 === 0 ? 'bg-gray-50' : ''">
                <td class="px-4 py-2 w-10">{{ index + 1 }}</td>
                <td class="px-4 py-2">{{ seller.name }}</td>
                <td class="px-4 py-2 text-right">{{ seller.score }} points</td>
                <td class="px-4 py-2 text-right">{{ formatPrice(seller.rewardAmount) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-if="isPresent(stores)">
        <div class="mb-4 text-xl font-bold">📊 Détail du classement réseau</div>
        <div class="overflow-x-auto max-h-80 overflow-y-auto shadow-lg bg-gray-50 relative">
          <table class="table-auto w-full text-left border-collapse border text-sm">
            <tbody>
              <tr v-for="(store, index) in stores" :key="store.id" :class="index % 2 === 0 ? 'bg-gray-50' : ''">
                <td class="px-4 py-2 w-10">{{ index + 1 }}</td>
                <td class="px-4 py-2">{{ store.name }}</td>
                <td class="px-4 py-2 text-right">{{ store.score }} points</td>
                <td class="px-4 py-2 text-right">{{ formatPrice(store.rewardAmount) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div>
      <div class="mt-8 mb-4 text-xl font-bold">📋 Détail des points par offre</div>
      <OffersTable :matchedContracts="matchedContracts" />
    </div>
  </div>
</template>

<script>
import { formatPrice, isPresent } from "@estaly/ui";
import { mapGetters } from "vuex";

import OffersTable from "@/components/challenge/OffersTable.vue";

export default {
  props: [
    "challenge",
    "sellers",
    "stores",
    "organizationSellers",
    "matchedContracts",
    "selectedStore",
    "storePerformance",
  ],

  components: { OffersTable },

  computed: {
    ...mapGetters("auth", ["hasAccessToAdminDashboard", "isResponsible"]),

    firstStoreSeller() {
      return this.sellers[0];
    },
    secondStoreSeller() {
      return this.sellers[1];
    },
    thirdStoreSeller() {
      return this.sellers[2];
    },

    firstOrganizationSeller() {
      return this.organizationSellers[0];
    },
    secondOrganizationSeller() {
      return this.organizationSellers[1];
    },
    thirdOrganizationSeller() {
      return this.organizationSellers[2];
    },

    groupedContracts() {
      if (!this.matchedContracts) return [];
      const grouped = this.matchedContracts.reduce((acc, contract) => {
        const category = contract.category;
        if (!acc[category]) acc[category] = { category, contracts: [] };
        acc[category].contracts.push(contract);
        return acc;
      }, {});
      return Object.values(grouped);
    },
  },

  methods: {
    formatPrice,
    isPresent,
    formatTermLength(contract) {
      if (contract.monthlyBilling) return "Mensuel";
      else if (contract.termLength == 1) return "1 an";
      return contract.termLength + " ans";
    },
  },
};
</script>
